import {
  type FieldValueEditProps,
  FieldValueEdit,
} from '@databases/fields/FieldValueEdit';
import { defaultStoreUtils } from '@databases/redux';
import { addChangeToDraft } from '@databases/redux/changesets/actions';
import { type DatabaseValue } from '@databases/types/database';
import { DatabaseChangeType } from '@databases/types/redux';
import {
  type DatabaseChange,
  type ValueChange,
} from '@databases/utils/database/changesets';
import {
  fieldShouldSaveOnBlur,
  getConvertedDatabaseValue,
  getDatabaseEntryRequired,
  getDatabaseFieldRequired,
} from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react';

import {
  clearCellEditingSessionChanges,
  getPendingChangesForField,
  addChangeToEditSession,
  startCellEditingSessionForField,
} from './cell-editing-session-store';

export interface DatabaseValueEditProps
  extends Omit<
    FieldValueEditProps,
    'field' | 'value' | 'onChange' | 'addDraftedDatabaseUpdate'
  > {
  contentId: string;
  fieldId: string;
  entryId: string;
  handleChange?: (value: DatabaseValue, change: ValueChange | null) => void;
  currentSortFieldId?: string;
}

/**
 * Renders a database value. The right view component is automatically chosen based on the field type of the value.
 */
const DatabaseValueEdit: FunctionComponent<DatabaseValueEditProps> = ({
  contentId,
  fieldId,
  entryId,
  handleChange,
  currentSortFieldId,
  onFocus,
  onBlur,
  ...props
}) => {
  const dispatch = defaultStoreUtils.useDispatch();
  const [field, value] = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, {
      contentId,
    });
    const entry = getDatabaseEntryRequired(database, entryId);
    const field = getDatabaseFieldRequired(database, fieldId);
    const value = getConvertedDatabaseValue(database, field, entry, state);
    return [field, value] as const;
  });

  const [isFocused, setIsFocused] = useState(false);

  // Used for things like creating select options.
  const handleAddChangeToDraft = useCallback(
    (change: DatabaseChange) => {
      dispatch(addChangeToDraft(contentId, change));
    },
    [contentId, dispatch]
  );

  const isFieldInViewSort = useMemo(
    () => currentSortFieldId === field.id,
    [field.id, currentSortFieldId]
  );

  const handleDispatchSetValue = useCallback(
    (value: DatabaseValue, change: ValueChange | null) => {
      dispatch(
        addChangeToDraft(contentId, {
          type: DatabaseChangeType.SET_VALUE,
          fieldId: fieldId,
          entryId: entryId,
          value,
          change,
        })
      );
    },
    [contentId, dispatch, entryId, fieldId]
  );

  const defaultHandleChange = useCallback(
    (value: DatabaseValue, change: ValueChange | null) => {
      if (isFieldInViewSort || fieldShouldSaveOnBlur(field.type)) {
        addChangeToEditSession({ val: value, change });
      } else {
        handleDispatchSetValue(value, change);
      }
    },
    [isFieldInViewSort, field.type, handleDispatchSetValue]
  );

  const handleChangesFromStore = useCallback(() => {
    const changes = getPendingChangesForField(field.id, field.type);

    if (changes.length > 0) {
      changes.forEach(({ val, change }) => handleDispatchSetValue(val, change));
      clearCellEditingSessionChanges();
    }
  }, [field.id, field.type, handleDispatchSetValue]);

  const handleFocus = useCallback(() => {
    if (isFocused || (!isFieldInViewSort && !fieldShouldSaveOnBlur(field.type))) {
      return;
    }

    setIsFocused(true);
    startCellEditingSessionForField(field.id);

    onFocus?.();
  }, [field.id, field.type, isFieldInViewSort, isFocused, onFocus]);

  const handleBlur = useCallback(() => {
    handleChangesFromStore();

    onBlur?.();
    setIsFocused(false);
  }, [handleChangesFromStore, onBlur]);

  return (
    <FieldValueEdit
      {...props}
      contentId={contentId}
      entryId={entryId}
      field={field}
      value={value}
      onChange={handleChange || defaultHandleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      addChangeToDraft={handleAddChangeToDraft}
    />
  );
};

export default DatabaseValueEdit;
